import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAppointments } from '@/scheduling/services/use-appointments';
import { mapAppointmentsToDayList, type DayListRow } from '@/scheduling/lib/calendar-mapping';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

/**
 * AppointmentSearch — the "Search appointments" typeahead in the scheduling
 * toolbar.
 *
 * Flutter parity: `calendar_screen.dart:2904-3134` (`searchWidget`). A
 * 200ms-debounced TypeAheadField that queries the appointment LIST endpoint
 * (`AppointmentListUseCase`) with `searchText`, `filter=Day`, a date window of
 * `selectedDate − 30 days` → `selectedDate` end-of-day, all providers, page
 * size 20 — matching on patient name / MRN / provider / email / phone
 * (backend-side). Selecting a suggestion opens that appointment
 * (`Navigator.push(ViewAppointmentBaseScreen)`), which in React is the
 * `/scheduling/appointment/{id}` route.
 *
 * Self-contained (own query + navigation) like `PracticeSwitcher`. Hidden for
 * Phlebotomists by the frame (`searchWidget` returns an empty Container for
 * `isPhlebotomistRole`).
 *
 * Implementation note: this is an always-visible input with a results panel
 * below — NOT a Popover/Command (those trap focus and would fight the
 * always-mounted input). Mirrors the toolbar's existing absolute-dropdown
 * idiom (the Create menu) + the Flutter-web-workaround simplification rule.
 */

function ymd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatRowDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function AppointmentSearch({
  organizationId,
  practiceId,
  selectedDate,
  providerIds,
}: {
  organizationId: string;
  practiceId: string;
  selectedDate: Date;
  providerIds: string[];
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Small screens (Antonin, 13 Oct): the search folds to its icon; a click opens the field and puts the cursor in it.
  // It folds back when it loses focus empty, or on Escape.
  // Also when its own row runs short (15 Oct): with the booking panel open, at 1280 the date row lost ~150 px and the
  // Practitioners switch slid under the field. The row is measured, not the window.
  const [rowShort, setRowShort] = useState(false);
  const rowObserver = useRef<ResizeObserver | null>(null);
  const watchRow = useCallback((el: HTMLElement | null) => {
    rowObserver.current?.disconnect();
    rowObserver.current = null;
    const row = el?.closest<HTMLElement>('[data-calendar-date-row]');
    if (!row || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setRowShort(entry.contentRect.width < 1000));
    ro.observe(row);
    rowObserver.current = ro;
  }, []);
  const compact = useNarrowWindow(1280) || rowShort;
  const [opened, setOpened] = useState(false);
  const folded = compact && !opened && !text;
  useEffect(() => { if (compact && opened) inputRef.current?.focus(); }, [compact, opened]);

  const debounced = useDebounce(text, 200);
  const trimmed = debounced.trim();

  // Flutter window: selectedDate − 30 days 00:00 → selectedDate 23:59:59
  // (`calendar_screen.dart:3000-3009`). No-Z local-clock strings (the .NET
  // model binder misreads the `Z`/UTC suffix — repo wire-shape rule).
  const range = useMemo(() => {
    const end = new Date(selectedDate);
    const start = new Date(selectedDate);
    start.setDate(start.getDate() - 30);
    return {
      startDate: `${ymd(start)}T00:00:00.000`,
      endDate: `${ymd(end)}T23:59:59.000`,
    };
  }, [selectedDate]);

  // Gated on non-empty text — `useAppointments(null)` is disabled, so no query
  // fires for an empty box (Flutter `suggestionsCallback` returns [] on blank).
  const params =
    trimmed && practiceId
      ? {
          organizationId,
          practiceId,
          filter: 'Day' as const,
          startDate: range.startDate,
          endDate: range.endDate,
          practitioners: providerIds,
          searchText: trimmed,
          pageNumber: 1,
          pageSize: 20,
        }
      : null;

  const { data, isFetching } = useAppointments(params);
  const rows = useMemo<DayListRow[]>(() => mapAppointmentsToDayList(data), [data]);

  const goToAppointment = (row: DayListRow) => {
    // All four ids are required by the view route's PreLoadAppointmentDetails
    // (gated on every id present). Skip rows missing any — defensive against
    // shape drift, same guard as scheduling-page's openPopupForAppointment.
    if (!row.patientId || !row.practitionerId || !row.appointmentProfileId) return;
    const qs = new URLSearchParams({
      patientId: row.patientId,
      providerId: row.practitionerId,
      profileId: row.appointmentProfileId,
    });
    // Return to wherever search was invoked from (calendar/patient/etc.).
    qs.set('from', location.pathname + location.search);
    navigate(`/scheduling/appointment/${row.id}?${qs.toString()}`);
    setText('');
    setFocused(false);
  };

  // Show the panel only while a search is active. The panel itself returns
  // null on settled-empty / error — Flutter parity (`hideOnEmpty: true`,
  // `hideOnError: true`, `hideOnLoading: false`, calendar_screen.dart:2953-2955).
  const active = focused && trimmed.length > 0;

  // Close on outside click (matches the Create-menu idiom in this toolbar).
  useEffect(() => {
    if (!active) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [active]);

  if (folded) {
    return (
      <button ref={watchRow} type="button" aria-label="Search appointments" data-search-folded onClick={() => setOpened(true)}
        className="grid size-8 flex-shrink-0 place-items-center rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] text-text-secondary hover:bg-grey-100 hover:text-text-primary">
        <Search className="h-4 w-4" aria-hidden />
      </button>
    );
  }

  return (
    <div ref={(el) => { wrapRef.current = el; watchRow(el); }} className="relative flex-1 max-w-[248px]">
      <Search className="pointer-events-none absolute left-3 top-2 h-4 w-4 text-text-hint" />
      <input
        ref={inputRef}
        onBlur={() => { if (!text) setOpened(false); }}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setText('');
            setFocused(false);
            setOpened(false);
          }
        }}
        placeholder="Search appointments"
        aria-label="Search appointments"
        className="h-8 w-full rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] pl-8 pr-3 [font:var(--type-body)] text-[var(--ink-body)] focus:border-brand-primary focus:outline-none"
      />

      {active && (
        <SearchResultsPanel
          rows={rows}
          isFetching={isFetching}
          onSelect={goToAppointment}
        />
      )}
    </div>
  );
}

/**
 * Presentational results dropdown — pure, props-driven (story-able for every
 * state without a network round-trip).
 *
 * Flutter parity on visibility (`hideOnEmpty`/`hideOnError`/`hideOnLoading`):
 *   - fetching            → loading row
 *   - has rows            → the list
 *   - settled empty/error → null (panel hidden, NOT a "no results" message)
 */
export function SearchResultsPanel({
  rows,
  isFetching,
  onSelect,
}: {
  rows: DayListRow[];
  isFetching: boolean;
  onSelect: (row: DayListRow) => void;
}) {
  // hideOnEmpty + hideOnError: nothing to show and not loading → render nothing.
  if (!isFetching && rows.length === 0) return null;

  return (
    <div
      role="listbox"
      className="absolute left-0 top-full z-30 mt-1 max-h-[320px] w-[304px] overflow-y-auto rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] py-1 shadow-overlay scrollbar-hide"
    >
      {isFetching && (
        <div className="px-3 py-4 text-center [font:var(--type-body)] text-[var(--ink-meta)]">
          Searching…
        </div>
      )}
      {!isFetching &&
        rows.map((row) => (
          <button
            key={row.id}
            role="option"
            type="button"
            // onMouseDown + preventDefault: fire selection BEFORE the input's
            // blur tears the panel down (without preventDefault the input
            // blurs first on some browsers and the click never lands).
            // Flutter uses onPointerDown for the same reason (:3092).
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(row);
            }}
            className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-grey-100"
          >
            <Avatar size="sm" className="mt-1 shrink-0">
              {row.patientAvatar ? (
                <AvatarImage src={row.patientAvatar} alt={row.patientName} />
              ) : null}
              <AvatarFallback className="bg-grey-200 text-size-2xs text-text-muted">
                {row.patientName.trim().charAt(0).toUpperCase() || 'P'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-text-primary">
                {row.patientName}
              </p>
              <p className="truncate text-xs text-text-primary">
                {row.doctorName}
                {row.doctorSpecialty ? ` • ${row.doctorSpecialty}` : ''}
              </p>
              <p className="truncate text-xs text-text-muted">
                {formatRowDate(row.startISO)} • {row.timeRange}
              </p>
            </div>
          </button>
        ))}
    </div>
  );
}

/** True while the window is narrower than `px` (follows resizes). */
function useNarrowWindow(px: number): boolean {
  const query = `(max-width: ${px - 1}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return narrow;
}
